import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardContent,
  cardContentClasses,
  CardHeader,
  cardHeaderClasses,
  Link,
  type Theme,
  useTheme
} from '@mui/material';

import { ProcessTalentResponseDTO, TalentDTO } from '@/api/generated';
import { toTalentTestSession } from '@/root/linker';
import TestSession from '@/views/TestSession/TestSession';

import Subheader from './Subheader';
import { TalentProcessContext } from './TalentProcessContext';
import TestSessionPreview from './TestSessionPreview';
import Title from './Title';

const ProcessCard = ({
  process,
  isExpandedView,
  talent
}: {
  talent: TalentDTO;
  process: ProcessTalentResponseDTO;
  isExpandedView: boolean;
}) => {
  const theme = useTheme();
  const { spacing } = theme;
  const { rootStyles, headerStyles, contentStyles } = getProcessCardStyles(theme);
  const testSessionSummaries = process.testSessionSummaries ?? [];

  return (
    <TalentProcessContext.Provider value={{ process, talent }}>
      <Card key={process.id} elevation={0} sx={rootStyles}>
        <CardHeader title={<Title />} subheader={<Subheader />} sx={headerStyles} />
        <CardContent component="dl" sx={contentStyles}>
          {isExpandedView ? (
            <TestSession testSessionId={testSessionSummaries[0].id} />
          ) : (
            testSessionSummaries.map(testSession => (
              <Link
                key={testSession.id}
                component={RouterLink}
                to={toTalentTestSession({ testSessionId: testSession.id!, talentId: talent.id! })}
                sx={{
                  display: 'block',
                  flexGrow: 1,
                  textDecoration: 'none',
                  marginBottom: spacing(1),
                  ':last-child': { marginBottom: 0 }
                }}
              >
                <TestSessionPreview testSession={testSession} />
              </Link>
            ))
          )}
        </CardContent>
      </Card>
    </TalentProcessContext.Provider>
  );
};

export default ProcessCard;

const getProcessCardStyles = (theme: Theme) => ({
  rootStyles: {
    backgroundColor: theme.palette.background.default,
    textAlign: 'start',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    ':last-child': { marginBottom: 0 }
  },

  headerStyles: {
    padding: 0,
    marginBottom: theme.spacing(1),

    [`& .${cardHeaderClasses.title}`]: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: theme.spacing(2)
    },

    [`& .${cardHeaderClasses.subheader}`]: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center'
    }
  },

  contentStyles: {
    [`&, &.${cardContentClasses.root}:last-child`]: {
      margin: 0,
      padding: 0
    }
  }
});
